/**
 * ResentLinkBadge — both ends of a re-send on the Status cell: "Re-sent as
 * #325900" on the killed drop, "Re-send of #325852" on the new one. Opens the
 * other drop when it exists on SMSWiser.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';

import type { PreparedDrop, PreparedDropResendLink } from '@/features/smswiser-campaigns/types/workspace.types';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { resentLabel } from '@/features/resend-killed-drops/planResend';

const CHIP =
  'inline-flex shrink-0 items-center gap-1 rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary';

function tooltip(prefix: string, link: PreparedDropResendLink, ownWorkDate: string): string {
  const title = link.sentTitle ?? 'a new drop';
  const otherDay = link.workDate && link.workDate.slice(0, 10) !== ownWorkDate.slice(0, 10);
  return `${prefix} ${title}${otherDay ? ` — on the ${formatDateDMY(link.workDate!)} list` : ''}`;
}

const Chip: FC<{ text: string; title: string; uniqueId?: string | null; returnTo?: string }> = ({
  text,
  title,
  uniqueId,
  returnTo,
}) => {
  const body = (
    <>
      <RotateCcw className="h-2.5 w-2.5" aria-hidden />
      {text}
    </>
  );
  if (!uniqueId) {
    return (
      <span className={CHIP} title={title}>
        {body}
      </span>
    );
  }
  return (
    <Link
      to={`/drops/${encodeURIComponent(uniqueId)}`}
      state={returnTo ? { returnTo } : undefined}
      className={`${CHIP} hover:bg-primary/20`}
      title={`${title} · open`}
    >
      {body}
    </Link>
  );
};

export const ResentLinkBadge: FC<{ drop: PreparedDrop; returnTo?: string }> = ({ drop, returnTo }) => {
  if (drop.resentAs) {
    return (
      <Chip
        text={resentLabel(drop.resentAs)}
        title={tooltip('Re-sent as', drop.resentAs, drop.workDate)}
        uniqueId={drop.resentAs.sentDropUniqueId}
        returnTo={returnTo}
      />
    );
  }
  if (!drop.resentFrom) return null;
  const id = drop.resentFrom.sentDropId;
  return (
    <Chip
      text={id != null ? `Re-send of #${id}` : 'Re-send'}
      title={tooltip('Re-send of the killed drop', drop.resentFrom, drop.workDate)}
      uniqueId={drop.resentFrom.sentDropUniqueId}
      returnTo={returnTo}
    />
  );
};
